import React, { useEffect, useRef, useState } from 'react'
import { MagnifyingGlassIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { opcjeModeli, szukajModeli } from '../../api/falowniki'
import { szukajMagazynow, opcjeSprzetu, modelSprzetu } from '../../api/oferta'
import { magazineManufacturers } from '../../filtersSchema'
import Rozwijacz from './Rozwijacz'
import { ProductImage } from '../ProductImage'
import { buildModelUrl } from '../../utils/modelUrl'

/**
 * Wybór sprzętu z oferty: KONKRETNY model albo samo zawężenie.
 *
 * ═══ DWIE DROGI, BO OFERTY SĄ RÓŻNIE PRECYZYJNE ═══
 * Gdy oferta podaje pełny model, najszybciej wpisać go w pole z lupą. Gdy podaje
 * tylko „Deye hybrydowy LV 8 kW”, użytkownik wybiera producenta, serię i moc,
 * a backend liczy zakres dla wszystkich pasujących modeli. To drugie nie jest
 * gorszą odpowiedzią, tylko uczciwym odbiciem tego, co jest w ofercie.
 *
 * Wartość: `{ producent, seria, moc, model? }` albo null. `model` to konkretny model
 * z wyszukiwarki: wypełnia listy (producent, seria, moc, w miarę danych) i stoi pod nimi.
 * Raport liczy wtedy ceny tego modelu. Każda zmiana na listach go usuwa i raport liczy
 * zakres dla wszystkich pasujących modeli (decyzja właściciela 2026-09-28).
 * Wzór wyglądu i zachowania: `components/kreator/WyborModelu.jsx`.
 *
 * `listaModeli` (2026-11-02, „Sprzęt w ofercie” na Rynku): tu ma paść konkretny sprzęt, a nie zakres cen, więc pod listami
 * stoi jeszcze lista modeli, a teksty o liczeniu cen i podgląd najczęściej oferowanego znikają.
 */

const KLASA_POLA = `border-2 border-blue-200 bg-blue-50/40 rounded-xl px-3 py-2.5 text-sm w-full transition
                    hover:border-blue-300 focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-500/25
                    placeholder:text-gray-400 disabled:opacity-50`

const JEDNOSTKI = { inwerter: 'kW', panel: 'W', magazyn: 'kWh' }
const PRODUCENCI_MAGAZYNOW = Object.keys(magazineManufacturers).sort((a, b) => a.localeCompare(b, 'pl'))

/** „1 oferta”, „3 oferty”, „5 ofert”, „22 oferty”. */
const ofert = (n) => {
  if (n === 1) return '1 oferta'
  const j = n % 10, d = n % 100
  return j >= 2 && j <= 4 && (d < 12 || d > 14) ? `${n} oferty` : `${n} ofert`
}

/**
 * Model pod listami: przypięty z wyszukiwarki (`wybrany`, z przyciskiem usuwania) albo
 * podgląd najczęściej oferowanego pasującego. Zdjęcie i nazwa otwierają stronę modelu
 * w NOWEJ karcie: formularz ofert nie jest nigdzie zapisywany.
 */
const KartaModelu = ({ kategoria, id, nazwa, img, opis, wybrany = false, onUsun }) => {
  const url = buildModelUrl(kategoria, { id })
  return (
    <div className={`mt-3 flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 ${wybrany ? 'border-blue-500 bg-blue-50' : 'border-blue-100 bg-white'}`}>
      <a href={url} target="_blank" rel="noopener noreferrer" title="Strona modelu w nowej karcie"
         className="relative block h-14 w-14 flex-shrink-0 rounded-lg border border-blue-200 bg-white p-1 transition hover:border-blue-500 hover:shadow">
        <ProductImage src={img} alt={nazwa} productType={kategoria} className="h-full w-full object-contain" />
        {wybrany && <CheckCircleIcon className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full bg-white text-blue-600" />}
      </a>
      <span className="min-w-0 flex-1">
        <a href={url} target="_blank" rel="noopener noreferrer"
           className="block truncate text-sm font-semibold text-gray-900 hover:text-blue-700" title={nazwa}>{nazwa} <span className="text-blue-600">↗</span></a>
        <span className="block text-xs text-gray-500 leading-5">{opis}</span>
      </span>
      {onUsun && (
        <button type="button" onClick={onUsun} aria-label="Usuń wybrany model"
                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800">
          <XMarkIcon className="w-4 h-4" /> Usuń
        </button>
      )}
    </div>
  )
}

// Pole mocy w odpowiedzi `GET /api/sprawdz_oferte/model` dla danej kategorii formularza.
const POLE_MOCY = { inwerter: 'moc_kw', panel: 'moc_w', magazyn: 'pojemnosc_kwh' }

// Teksty zależne od miejsca: domyślne są dla „Sprawdź ofertę”, narzędzie zgłoszeń OSD podaje własne.
const TEKSTY_OFERTY = {
  albo: 'albo wybierz, co wiesz z oferty',
  wybrany: 'Wybrany model. Liczymy ceny tego modelu; zmiana na listach go usunie.',
}

const WyborSprzetu = ({ kategoria, wartosc, onZmiana, placeholder, etykietaMocy, fazy, onModel, teksty = TEKSTY_OFERTY, listaModeli = false }) => {
  const jednostka = JEDNOSTKI[kategoria]
  const [opcje, setOpcje] = useState({ producenci: [], moce: [] })
  // Listy zależne: po wyborze producenta tylko to, co istnieje w katalogu i ma cenę.
  const [zalezne, setZalezne] = useState(null)
  const [fraza, setFraza] = useState('')
  const [wyniki, setWyniki] = useState([])
  const [szuka, setSzuka] = useState(false)
  const ostatnie = useRef(0)
  // Id modelu, którego opis właśnie dociągamy; zmiana na listach go zeruje, żeby
  // spóźniona odpowiedź nie przywróciła usuniętego modelu.
  const doWypelnienia = useRef(null)

  const wybrany = wartosc?.model
  const filtry = wartosc || {}

  useEffect(() => {
    if (kategoria === 'magazyn') { setOpcje({ producenci: PRODUCENCI_MAGAZYNOW, moce: [] }); return }
    // Oferty dotyczą domów: mikroinwertery (0,1 kW) i falowniki farm (350 kW) tylko
    // wydłużałyby listę. Pole z lupą nadal znajdzie każdy model.
    const wZakresie = (m) => kategoria !== 'inwerter' || (m >= 1 && m <= 50)
    opcjeModeli(kategoria).then((o) => setOpcje({ producenci: o.producenci || [], moce: (o.moce || []).filter(wZakresie) }))
      .catch(() => setOpcje({ producenci: [], moce: [] }))
  }, [kategoria])

  // Serie i moce liczone z katalogu dla wybranego producenta (i serii). Moc, której
  // na liście nie ma (oferta: SigenStor 15,06 kWh, katalog: 6, 9, 12 kWh), ZOSTAJE:
  // raport liczy wtedy cenę z najbliższych modeli tej marki przeliczoną na tę moc
  // (decyzja właściciela 2026-09-30). Wcześniej moc była czyszczona, a po odczycie pliku
  // znikał producent i raport pokazywał modele innych marek.
  useEffect(() => {
    if (!filtry.producent) { setZalezne(null); return undefined }
    let aktualne = true
    opcjeSprzetu(kategoria, filtry.producent, filtry.seria, filtry.moc, fazy).then((o) => {
      if (aktualne && o) setZalezne(o)
    })
    return () => { aktualne = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kategoria, filtry.producent, filtry.seria, filtry.moc, fazy])

  // Wartości przypiętego modelu dokładamy do list, gdyby ich tam nie było.
  const zModelem = (lista, v) => (v === '' || v == null || lista.map(String).includes(String(v)) ? lista : [...lista, v])
  const serie = wybrany && filtry.seria && !(zalezne?.serie || []).some((s) => s.value === filtry.seria)
    ? [...(zalezne?.serie || []), { value: filtry.seria, count: null }] : (zalezne?.serie || [])
  const moce = zModelem(zalezne ? zalezne.moce : opcje.moce, filtry.moc).slice().sort((a, b) => Number(a) - Number(b))
  // Moc z oferty, której ten producent nie ma w katalogu: cena będzie szacunkiem z jego innych modeli.
  const mocSpozaKatalogu = !wybrany && filtry.producent && filtry.moc && zalezne && zalezne.moce.length > 0
    && !zalezne.moce.map(String).includes(String(filtry.moc))

  const frazaGotowa = fraza.trim().length >= 3
  useEffect(() => {
    if (!frazaGotowa) { setWyniki([]); return undefined }
    const t = setTimeout(async () => {
      const moje = ++ostatnie.current
      setSzuka(true)
      const r = kategoria === 'magazyn'
        ? await szukajMagazynow(fraza.trim())
        : ((await szukajModeli(kategoria, { q: fraza.trim() }).catch(() => ({ wyniki: [] }))).wyniki || [])
      if (moje === ostatnie.current) { setWyniki(r); setSzuka(false) }
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fraza, kategoria])

  // Zmiana na listach usuwa przypięty model: od tej chwili liczymy wszystkie pasujące.
  const ustawFiltr = (klucz, v) => {
    doWypelnienia.current = null
    const { model, ...bezModelu } = filtry // eslint-disable-line no-unused-vars
    const nowe = { ...bezModelu, [klucz]: v }
    if (klucz === 'producent') { nowe.seria = ''; nowe.moc = nowe.moc || '' }
    const puste = !nowe.producent && !nowe.seria && !nowe.moc
    onZmiana(puste ? null : { producent: nowe.producent || '', seria: nowe.seria || '', moc: nowe.moc || '' })
  }

  /**
   * Wybór z wyszukiwarki wypełnia listy. Od razu to, co wiemy z wyniku (producent, moc),
   * potem seria i fazy z opisu modelu. Seria bywa nieznana: wtedy zostaje „nie wiem”,
   * a o cenie i tak decyduje przypięty model.
   */
  const wybierz = async (w) => {
    setFraza(''); setWyniki([])
    const model = { id: w.model_id, etykieta: w.nazwa, producent: w.producent, moc: w.moc, img: w.img_url || null }
    doWypelnienia.current = w.model_id
    onZmiana({ producent: w.producent || '', seria: '', moc: w.moc ?? '', model })
    const k = await modelSprzetu(kategoria, w.model_id)
    if (!k || doWypelnienia.current !== w.model_id) return
    const moc = k[POLE_MOCY[kategoria]] ?? w.moc ?? ''
    onZmiana({
      producent: k.producent || w.producent || '', seria: k.seria || '', moc,
      model: { ...model, moc, img: k.img_url || model.img },
    })
    if (onModel) onModel(k)
  }

  const bezModelu = () => {
    doWypelnienia.current = null
    onZmiana({ producent: filtry.producent || '', seria: filtry.seria || '', moc: filtry.moc || '' })
  }
  const najlepszy = !listaModeli && !wybrany && filtry.producent && filtry.moc ? zalezne?.najlepszy : null
  const modeleNaLiscie = (zalezne?.modele || []).map((m) => ({ value: String(m.model_id), label: m.nazwa, licznik: m.ofert }))
  if (wybrany && !modeleNaLiscie.some((m) => m.value === String(wybrany.id))) modeleNaLiscie.unshift({ value: String(wybrany.id), label: wybrany.etykieta })
  const wybierzZListy = (id) => {
    if (!id) { bezModelu(); return }
    const m = (zalezne?.modele || []).find((x) => String(x.model_id) === String(id))
    if (m) wybierz({ ...m, producent: filtry.producent })
  }

  return (
    <div>
      <div className="relative">
        <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input type="text" value={fraza} onChange={(e) => setFraza(e.target.value)}
               placeholder={placeholder} aria-label={placeholder}
               className={`${KLASA_POLA} pl-9`} />
      </div>
      {frazaGotowa && (
        <div className="mt-2 rounded-xl border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden shadow-lg ring-1 ring-black/5">
          {szuka && <p className="px-3 py-2.5 text-xs text-gray-400">Szukam…</p>}
          {!szuka && wyniki.length === 0 && (
            <p className="px-3 py-2.5 text-xs text-gray-500">Nie znaleźliśmy takiego modelu. Wybierz producenta i moc poniżej.</p>
          )}
          {!szuka && wyniki.map((w) => (
            <button key={w.model_id} type="button" onClick={() => wybierz(w)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-blue-50/60">
              <span className="h-10 w-10 flex-shrink-0 rounded-md border border-gray-100 bg-white p-0.5">
                <ProductImage src={w.img_url} alt="" productType={kategoria} className="h-full w-full object-contain" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-gray-900 truncate">{w.nazwa}</span>
                <span className="block text-xs text-gray-500">
                  {w.producent}{w.moc ? ` · ${String(w.moc).replace('.', ',')} ${jednostka}` : ''}
                </span>
              </span>
              {w.ofert != null && <span className="text-[11px] text-gray-400 flex-shrink-0">{ofert(w.ofert)}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 my-3">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{teksty.albo}</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="grid sm:grid-cols-3 gap-2">
        <Rozwijacz etykieta="Producent" placeholder="Wybierz" pusta="Nie wiem"
                   szukajPlaceholder="Szukaj producenta" wartosc={filtry.producent || ''}
                   opcje={opcje.producenci.map((p) => ({ value: p, label: p }))}
                   onZmiana={(v) => ustawFiltr('producent', v)} />
        <Rozwijacz etykieta="Seria" pusta="Nie wiem" szukajPlaceholder="Szukaj serii"
                   placeholder={filtry.producent && zalezne && serie.length === 0 ? 'brak w katalogu' : 'nie wiem'}
                   disabled={!filtry.producent || serie.length === 0} wartosc={filtry.seria || ''}
                   opcje={serie.map((s) => ({ value: s.value, label: s.value, licznik: s.count }))}
                   onZmiana={(v) => ustawFiltr('seria', v)} />
        {kategoria === 'magazyn' && !zalezne ? (
          // Bez producenta nie znamy listy pojemności, więc pole do wpisania w tym samym stylu.
          <label className="flex items-center gap-2 rounded-xl border-2 border-blue-200 bg-blue-50/40 px-3 py-1.5 transition
                            hover:border-blue-300 focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100 dark:focus-within:ring-blue-500/25">
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400">{etykietaMocy}</span>
              <input type="number" inputMode="decimal" min="0" step="0.1" value={filtry.moc || ''}
                     onChange={(e) => ustawFiltr('moc', e.target.value)} placeholder="np. 10"
                     className="block w-full bg-transparent text-sm font-semibold text-gray-900 tabular-nums placeholder:font-normal placeholder:text-gray-500 focus:outline-none" />
            </span>
            <span className="text-sm text-gray-400">kWh</span>
          </label>
        ) : (
          <Rozwijacz etykieta={etykietaMocy} placeholder="nie wiem" pusta="Nie wiem"
                     szukajPlaceholder={kategoria === 'magazyn' ? 'Wpisz pojemność, np. 10' : 'Wpisz moc, np. 470'}
                     wartosc={filtry.moc || ''}
                     opcje={moce.map((m) => ({ value: m, label: `${String(m).replace('.', ',')} ${jednostka}` }))}
                     onZmiana={(v) => ustawFiltr('moc', v)} />
        )}
      </div>
      {listaModeli && (
        <div className="mt-2">
          <Rozwijacz etykieta="Model z katalogu" pusta="Bez modelu" szukajPlaceholder="Szukaj modelu"
                     placeholder={!filtry.producent ? 'najpierw producent' : modeleNaLiscie.length ? 'wybierz' : 'brak w katalogu'}
                     disabled={!filtry.producent || modeleNaLiscie.length === 0} wartosc={wybrany ? String(wybrany.id) : ''}
                     opcje={modeleNaLiscie} onZmiana={wybierzZListy} />
        </div>
      )}
      {wybrany && (
        <KartaModelu kategoria={kategoria} id={wybrany.id} nazwa={wybrany.etykieta} img={wybrany.img} wybrany
                     opis={teksty.wybrany}
                     onUsun={bezModelu} />
      )}
      {najlepszy && (
        <KartaModelu kategoria={kategoria} id={najlepszy.model_id} nazwa={najlepszy.nazwa} img={najlepszy.img_url}
                     opis={zalezne.pasujace > 1
                       ? `Najczęściej oferowany z ${zalezne.pasujace} pasujących modeli. Liczymy zakres cen dla wszystkich.`
                       : 'Jedyny pasujący model w katalogu.'} />
      )}
      {!wybrany && filtry.producent && zalezne && zalezne.liczba === 0 && (
        <p className="mt-2 text-xs text-amber-700">
          Nie mamy w katalogu modeli z ceną dla tych filtrów. Zmień serię albo moc, albo wpisz model w pole z lupą.
        </p>
      )}
      {mocSpozaKatalogu && !listaModeli && (
        <p className="mt-2 text-xs leading-5 text-gray-500">
          W katalogu nie ma {filtry.producent} {kategoria === 'magazyn' ? 'o pojemności' : 'o mocy'}{' '}
          {String(filtry.moc).replace('.', ',')} {jednostka}. Cenę oszacujemy z najbliższych modeli tej marki,
          przeliczoną na {String(filtry.moc).replace('.', ',')} {jednostka}.
        </p>
      )}
      {!listaModeli && !wybrany && !najlepszy && !mocSpozaKatalogu && filtry.producent && (!zalezne || zalezne.liczba > 0) && (
        <p className="mt-2 text-xs text-gray-500">
          Policzymy zakres cen dla wszystkich modeli {filtry.producent}
          {filtry.seria ? ` z serii ${filtry.seria}` : ''}
          {filtry.moc ? ` ${kategoria === 'magazyn' ? 'o pojemności' : 'o mocy'} ${String(filtry.moc).replace('.', ',')} ${jednostka}` : ''}
          {zalezne && !filtry.moc ? ` (pasuje ${zalezne.liczba})` : ''}.
        </p>
      )}
    </div>
  )
}

export default WyborSprzetu
